import { logger } from './logger';

export type StoredPlanner = {
  id: string;
  url?: string;
  title?: string;
  createdAt: string;
};

const STORAGE_KEY = 'schedules';

// Mutex to prevent race conditions when saving planners
let saveMutex: Promise<void> = Promise.resolve();

const normalizeLink = (link: unknown): StoredPlanner | null => {
  if (!link || typeof link !== 'object') return null;
  const id = (link as { id?: unknown }).id;
  if (typeof id !== 'string') return null;
  const createdAt =
    typeof (link as { createdAt?: unknown }).createdAt === 'string'
      ? (link as { createdAt: string }).createdAt
      : new Date().toISOString();
  const url =
    typeof (link as { url?: unknown }).url === 'string'
      ? (link as { url: string }).url
      : undefined;
  const title =
    typeof (link as { title?: unknown }).title === 'string'
      ? (link as { title: string }).title
      : undefined;
  return {
    id,
    createdAt,
    ...(url ? { url } : {}),
    ...(title ? { title } : {}),
  };
};

const normalizeLinks = (links: unknown): StoredPlanner[] => {
  if (!Array.isArray(links)) return [];
  return links
    .map((l) => normalizeLink(l))
    .filter((l): l is StoredPlanner => !!l);
};

const readFromLocalStorage = (): StoredPlanner[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return normalizeLinks(JSON.parse(raw));
  } catch (error) {
    logger.error('Failed to read stored planners:', error);
    return [];
  }
};

const persistLinks = async (links: StoredPlanner[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(links));
  } catch (error) {
    logger.error('Failed to persist stored planners:', error);
  }
};

export const readStoredPlanners = async (): Promise<StoredPlanner[]> => {
  return readFromLocalStorage();
};

export const saveStoredPlanner = async (link: StoredPlanner) => {
  const normalized = normalizeLink(link);
  if (!normalized) return;

  saveMutex = saveMutex
    .then(async () => {
      const links = await readStoredPlanners();
      const existing = links.find((l) => l.id === normalized.id);
      const merged: StoredPlanner = {
        id: normalized.id,
        createdAt: normalized.createdAt,
        url: normalized.url ?? existing?.url,
        title: normalized.title ?? existing?.title,
      };
      const without = links.filter((l) => l.id !== normalized.id);
      await persistLinks([merged, ...without]);
    })
    .catch((error) => {
      logger.error('Failed to save stored planner:', error);
    });

  await saveMutex;
};

export const removeStoredPlanner = async (id: string) => {
  saveMutex = saveMutex
    .then(async () => {
      const links = await readStoredPlanners();
      await persistLinks(links.filter((l) => l.id !== id));
    })
    .catch((error) => {
      logger.error('Failed to remove stored planner:', error);
    });

  await saveMutex;
};

export const saveStoredPlanners = async (links: StoredPlanner[]) => {
  if (links.length === 0) return;

  saveMutex = saveMutex
    .then(async () => {
      const existing = await readStoredPlanners();
      const existingMap = new Map(existing.map((l) => [l.id, l]));

      for (const link of links) {
        const normalized = normalizeLink(link);
        if (normalized) {
          existingMap.set(normalized.id, normalized);
        }
      }

      const merged = Array.from(existingMap.values());
      await persistLinks(merged);
    })
    .catch((error) => {
      logger.error('Failed to save stored planners:', error);
    });

  await saveMutex;
};

export const clearStoredPlanners = async () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error('Failed to clear stored planners:', error);
  }
};
